import { Capacitor } from "@capacitor/core";
import { Share as CapacitorShare } from "@capacitor/share";
import { useMutation } from "@tanstack/react-query";

import { Models } from "@/api/types";
import { post } from "api";

type UseLegacyReferAFriendParams = {
  rewardsEnabled: boolean;
  platform: Models<"PlatformModel">;
  title: string;
  formattedPlatformName: string;
  copyToClipboard: (text: string) => void;
  isCopyingLink: boolean;
  referralUrl: string;
  setReferralUrl: (url: string) => void;
};

type UseLegacyReferAFriendResult = {
  shareLegacyReferral: () => void;
  handleCopyLink: () => void;
};

export function useLegacyReferAFriend({
  rewardsEnabled,
  platform,
  title,
  formattedPlatformName,
  copyToClipboard,
  isCopyingLink,
  referralUrl,
  setReferralUrl,
}: UseLegacyReferAFriendParams): UseLegacyReferAFriendResult {
  let shareText = `I had an amazing experience with ${formattedPlatformName}! Highly recommend checking them out—you'll love it!`;

  if (rewardsEnabled && title) {
    shareText =
      `Join ${formattedPlatformName}'s rewards program today and enjoy ${title} ` +
      "as a thank you for signing up! Tap to join.";
  }

  const mutation = useMutation({
    mutationFn: () => post("/referrals", {}),
    onSuccess: ({ referralUrl }) => {
      if (referralUrl) {
        setReferralUrl(referralUrl);

        if (Capacitor.isNativePlatform()) {
          CapacitorShare.share({
            text: `${shareText} ${referralUrl}`,
          })
            .then(() =>
              console.log(`Successful share. Referral URL: ${referralUrl}`)
            )
            .catch((error) =>
              console.log(`Error sharing. Referral URL: ${referralUrl}`, error)
            );

          return;
        }

        if (navigator.share) {
          navigator
            .share({
              text: `${shareText} ${referralUrl}`,
            })
            .then(() =>
              console.log(`Successful share. Referral URL: ${referralUrl}`)
            )
            .catch((error) =>
              console.log(`Error sharing. Referral URL: ${referralUrl}`, error)
            );

          return;
        }

        window.location.href = `mailto:?body=${encodeURIComponent(shareText)}`;
      }
    },
  });

  const copyLinkMutation = useMutation({
    mutationFn: () => post("/referrals", {}),
    onSuccess: ({ referralUrl }) => {
      if (referralUrl) {
        setReferralUrl(referralUrl);
        copyToClipboard(referralUrl);
      }
    },
    onError: (error) => {
      console.log("Error generating referral link for copy: ", error);
    },
  });

  const shareLegacyReferral = () => mutation.mutate();

  const handleCopyLink = () => {
    if (!referralUrl) {
      // Generate and copy the link
      copyLinkMutation.mutate();
    } else {
      // Copy existing link immediately
      copyToClipboard(referralUrl);
    }
  };

  return {
    shareLegacyReferral,
    handleCopyLink,
  };
}
